import React, { createContext, useContext, useState, useCallback } from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaTimes, FaExclamationCircle } from 'react-icons/fa';
import '../styles/Toast.css';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
};

const ToastItem = ({ toast, onRemove }) => {
  const getIcon = () => {
    switch (toast.type) {
      case 'success': return <FaCheckCircle className="toast-icon toast-icon-success" />;
      case 'error': return <FaExclamationCircle className="toast-icon toast-icon-error" />;
      case 'warning': return <FaExclamationTriangle className="toast-icon toast-icon-warning" />;
      default: return <FaInfoCircle className="toast-icon toast-icon-info" />;
    }
  };

  return (
    <div className={`toast toast-${toast.type}`}>
      <div className="toast-content">
        {getIcon()}
        <div className="toast-text">
          {toast.title && <div className="toast-title">{toast.title}</div>}
          <div className="toast-message">{toast.message}</div>
        </div>
      </div>
      <button className="toast-close" onClick={() => onRemove(toast.id)}>
        <FaTimes />
      </button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((options) => {
    const toast = {
      id: Date.now() + Math.random(),
      type: options.type || 'info',
      title: options.title,
      message: options.message,
      duration: options.duration || 5000,
    };
    setToasts(prev => {
      const next = [...prev, toast];
      return next.length > 3 ? next.slice(-3) : next;
    });
    if (toast.duration > 0) {
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== toast.id)), toast.duration);
    }
    return toast.id;
  }, []);

  const removeToast = useCallback((id) => setToasts(prev => prev.filter(t => t.id !== id)), []);
  const showToast = useCallback((message, type = 'info', duration = 5000) => addToast({ message, type, duration }), [addToast]);
  const success = useCallback((message, title) => addToast({ type: 'success', message, title }), [addToast]);
  const error = useCallback((message, title) => addToast({ type: 'error', message, title }), [addToast]);
  const warning = useCallback((message, title) => addToast({ type: 'warning', message, title }), [addToast]);
  const info = useCallback((message, title) => addToast({ type: 'info', message, title }), [addToast]);

  return (
    <ToastContext.Provider value={{ showToast, success, error, warning, info, removeToast }}>
      {children}
      <div className="toast-container">
        {toasts.map(t => <ToastItem key={t.id} toast={t} onRemove={removeToast} />)}
      </div>
    </ToastContext.Provider>
  );
};
